import { lazy, Suspense } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Welcome from './components/Welcome';
import Reviews from './components/Reviews';
import Services from './components/Services';
import Projects from './components/Projects';
import BookingCTA from './components/BookingCTA';

const ServiceAreaMap = lazy(() => import('./components/ServiceAreaMap'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const Footer = lazy(() => import('./components/Footer'));

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://hornpmc.ca';

const homeSeo = {
  title: 'Burlington Property Maintenance & Construction | Horn PMC',
  description:
    'Horn PMC – Burlington\'s trusted experts for property maintenance, deck & fence construction, and home renovations. Serving Burlington, Oakville, Hamilton & area. Call 905-869-4676 for a free estimate.',
  canonical: `${siteUrl}/`,
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Horn Property Maintenance and Construction',
  alternateName: 'Horn PMC',
  telephone: '+19058694676',
  url: siteUrl,
  description:
    'Property maintenance, deck and fence construction, and home renovations in Burlington, Ontario.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Burlington',
    addressRegion: 'ON',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.3255,
    longitude: -79.799,
  },
  areaServed: [
    'Burlington, ON',
    'Oakville, ON',
    'Hamilton, ON',
    'Mississauga, ON',
    'Milton, ON',
    'Stoney Creek, ON',
    'Grimsby, ON',
    'Ancaster, ON',
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  sameAs: [],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: homeSeo.title,
  description: homeSeo.description,
  url: homeSeo.canonical,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Horn PMC',
    url: siteUrl,
  },
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Horn PMC',
  alternateName: 'Horn Property Maintenance and Construction',
  url: siteUrl,
  description:
    'Burlington\'s trusted experts for property maintenance, deck & fence construction, and home renovations.',
  inLanguage: 'en-CA',
  publisher: {
    '@type': 'LocalBusiness',
    name: 'Horn Property Maintenance and Construction',
    url: siteUrl,
  },
};

export default function Home() {
  return (
    <>
      <title>{homeSeo.title}</title>
      <meta name="description" content={homeSeo.description} />
      <link rel="canonical" href={homeSeo.canonical} />
      <meta property="og:title" content={homeSeo.title} />
      <meta property="og:description" content={homeSeo.description} />
      <meta property="og:url" content={homeSeo.canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={homeSeo.title} />
      <meta name="twitter:description" content={homeSeo.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <main style={{ fontFamily: 'Inter, sans-serif' }}>
        <Navbar />
        <Hero />
        <Welcome />
        <Reviews />
        <Services />
        <Projects />
        <BookingCTA />
        <Suspense fallback={<div className="h-96 bg-white" />}>
          <ServiceAreaMap />
        </Suspense>
        <Suspense fallback={<div className="h-64 bg-[#F7F5F1]" />}>
          <ContactForm />
        </Suspense>
        <Suspense fallback={<div className="h-48 bg-[#2B5741]" />}>
          <Footer />
        </Suspense>
      </main>
    </>
  );
}